import styled from 'styled-components';
import React, { Dispatch, SetStateAction, useEffect } from 'react';

import { Feedback } from '@shared/constants/feedback';
import { CarouselItem } from '@components/molecules';

type CarouselProps = {
  gap: number;
  slides: Feedback[];
  sideWidth: number;
  centerWidth: number;
  currentIndex: number;
  isTransitioning: boolean;
  setSlides: Dispatch<SetStateAction<Feedback[]>>;
  setCurrentIndex: Dispatch<SetStateAction<number>>;
  setIsTransitioning: Dispatch<SetStateAction<boolean>>;
};

const Carousel: React.FC<CarouselProps> = ({
  gap,
  slides,
  setSlides,
  sideWidth,
  centerWidth,
  currentIndex,
  setCurrentIndex,
  isTransitioning,
  setIsTransitioning,
}) => {
  const calculateTranslateX = () => {
    const totalVisibleWidth = centerWidth + 2 * sideWidth + 2 * gap;
    const centerOffset = (totalVisibleWidth - centerWidth) / 1.955;
    return -(currentIndex * (sideWidth + gap)) + centerOffset;
  };

  useEffect(() => {
    if (currentIndex === 1) {
      const targetInd = slides.length + currentIndex;
      setSlides([...slides, ...slides]);
      setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(targetInd);
      }, 500);
    }
    if (currentIndex === slides.length - 2) {
      setSlides([...slides, ...slides]);
    }
  }, [currentIndex]);

  return (
    <CarouselContainer>
      <Cover />

      <CarouselTrack
        $gap={gap}
        $transition={isTransitioning}
        $translateX={calculateTranslateX()}
      >
        {slides.map((feedback, index) => {
          const isCenter = index === currentIndex;

          return (
            <CarouselItem
              feedback={feedback}
              centerWidth={centerWidth}
              isTransitioning={isTransitioning}
              sideWidth={sideWidth}
              isCenter={isCenter}
              index={index}
              key={index}
            />
          );
        })}
      </CarouselTrack>
    </CarouselContainer>
  );
};

const CarouselContainer = styled.div`
  position: relative;
  overflow: hidden;
  height: 500px;
  width: 100%;
`;

const Cover = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;

  background: linear-gradient(to right, #ffffff, #ffffff00, #ffffff00, #ffffff);

  z-index: 1;
`;

const CarouselTrack = styled.div<{
  $translateX: number;
  $transition: boolean;
  $gap: number;
}>`
  display: flex;
  height: 100%;
  width: max-content;
  gap: ${({ $gap }) => $gap}px;

  align-items: center;

  transform: ${({ $translateX }) => `translateX(${$translateX}px)`};
  transition: ${({ $transition }) => ($transition ? '0.3s' : 'none')};
`;

export default Carousel;
